import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useGameStore, setOnNumberSetCallback } from '../store/gameStore';
import { useAuthStore } from '../store/authStore';
import { useKeyboard } from '../hooks/useKeyboard';
import { puzzlesApi, progressApi } from '../api/client';
import { markPuzzleCompletedLocally } from '../lib/localProgress';
import { Puzzle, Difficulty } from '../types';
import Board from '../components/Board/Board';
import NumberPad from '../components/Board/NumberPad';
import GameStatus from '../components/Game/GameStatus';
import GameControls from '../components/Game/GameControls';
import CompletionModal from '../components/Game/CompletionModal';
import PauseOverlay from '../components/Game/PauseOverlay';

const MAX_HINTS = 3;

// Helper to get level number from puzzle (handles both snake_case and camelCase)
const getLevelNumber = (puzzle: Puzzle | null): number => {
  if (!puzzle) return 0;
  const levelNum = (puzzle as any).level_number ?? puzzle.levelNumber;
  // Ensure we return a valid number
  return typeof levelNum === 'number' ? levelNum : 0;
};

export default function GamePage() {
  const { puzzleId } = useParams<{ puzzleId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const {
    initGame,
    currentState,
    hintsUsed,
    mistakes,
    timeSpent,
    isComplete,
    useHint,
    reset,
  } = useGameStore();

  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCells, setErrorCells] = useState<Set<number>>(new Set());
  const [completionScore, setCompletionScore] = useState<number | undefined>();
  const [showCompletion, setShowCompletion] = useState(false);
  const [nextPuzzleId, setNextPuzzleId] = useState<number | undefined>();

  // Enable keyboard controls
  useKeyboard();

  // Load puzzle
  useEffect(() => {
    const loadPuzzle = async () => {
      if (!puzzleId) return;

      setIsLoading(true);
      setError(null);
      setNextPuzzleId(undefined);

      try {
        const response = await puzzlesApi.getById(parseInt(puzzleId, 10));
        const puzzleData = response.puzzle;

        setPuzzle(puzzleData);

        // Initialize game with saved progress if available
        initGame(
          puzzleData.puzzle,
          '', // Solution will be validated server-side
          puzzleData.progress ? {
            currentState: puzzleData.progress.currentState,
            notes: puzzleData.progress.notes || {},
            timeSpent: puzzleData.progress.timeSpent || 0,
            hintsUsed: puzzleData.progress.hintsUsed || 0,
            mistakes: puzzleData.progress.mistakes || 0,
          } : undefined
        );

        // Find next puzzle in same difficulty
        const currentLevelNum = (puzzleData as any).level_number ?? puzzleData.levelNumber;
        const allPuzzles = await puzzlesApi.getList(puzzleData.difficulty);
        const nextPuzzle = allPuzzles.puzzles.find((p: Puzzle) => {
          const pLevelNum = (p as any).level_number ?? p.levelNumber;
          return pLevelNum === currentLevelNum + 1;
        });
        if (nextPuzzle) {
          setNextPuzzleId(nextPuzzle.id);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : '加载关卡失败';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadPuzzle();
  }, [puzzleId, initGame]);

  // Auto-save progress periodically
  useEffect(() => {
    if (!isAuthenticated || !puzzleId || !puzzle || isComplete) return;

    const saveProgress = async () => {
      try {
        await progressApi.save(parseInt(puzzleId, 10), {
          currentState,
          timeSpent,
          hintsUsed,
          mistakes,
        });
      } catch (err) {
        console.error('Failed to save progress:', err);
      }
    };

    const interval = setInterval(saveProgress, 30000); // Save every 30 seconds

    // Save on unmount
    return () => {
      clearInterval(interval);
      saveProgress();
    };
  }, [isAuthenticated, puzzleId, puzzle, currentState, timeSpent, hintsUsed, mistakes, isComplete]);

  // Check if all cells are filled correctly
  const checkGameCompletion = useCallback(async () => {
    if (!puzzleId) return;

    // Get fresh state to ensure we have the latest values
    const state = useGameStore.getState();
    const currentState = state.currentState;
    const puzzleData = state.puzzle;

    // Check if all cells are filled
    if (currentState.includes('0')) return;

    // Validate all user-filled cells
    const validationPromises: Promise<{ position: number; isCorrect: boolean }>[] = [];
    const totalCells = puzzleData.length; // 16 for 4x4, 81 for 9x9

    for (let i = 0; i < totalCells; i++) {
      // Only validate cells that were originally empty (user-filled)
      if (puzzleData[i] === '0') {
        const value = parseInt(currentState[i], 10);
        validationPromises.push(
          puzzlesApi.validate(parseInt(puzzleId, 10), i, value)
            .then(res => ({ position: i, isCorrect: res.isCorrect }))
        );
      }
    }

    // If no cells to validate (shouldn't happen, but handle edge case)
    if (validationPromises.length === 0) {
      useGameStore.setState({ isComplete: true });
      return;
    }

    try {
      const results = await Promise.all(validationPromises);
      const allCorrect = results.every(r => r.isCorrect);

      if (allCorrect) {
        useGameStore.setState({ isComplete: true });
      }
    } catch (err) {
      console.error('Completion check failed:', err);
    }
  }, [puzzleId]);

  // Store puzzleId in ref for callback
  const puzzleIdRef = useRef(puzzleId);
  puzzleIdRef.current = puzzleId;

  // Handle number set callback (triggered for both keyboard and click input)
  const handleNumberSet = useCallback(async (cell: number, num: number, newState: string) => {
    const currentPuzzleId = puzzleIdRef.current;
    if (!currentPuzzleId) return;

    const puzzleData = useGameStore.getState().puzzle;

    // If cell is fixed, no need to validate
    if (puzzleData[cell] !== '0') return;

    // Validate the move
    try {
      const response = await puzzlesApi.validate(parseInt(currentPuzzleId, 10), cell, num);
      if (!response.isCorrect) {
        setErrorCells(new Set([cell]));
        useGameStore.getState().incrementMistake();

        // Clear error after animation
        setTimeout(() => setErrorCells(new Set()), 500);
      }
    } catch (err) {
      console.error('Validation failed:', err);
    }

    // Check if all cells are filled and validate completion
    if (!newState.includes('0')) {
      await checkGameCompletion();
    }
  }, [checkGameCompletion]);

  // Register the callback when component mounts
  useEffect(() => {
    setOnNumberSetCallback(handleNumberSet);
    return () => {
      setOnNumberSetCallback(null);
    };
  }, [handleNumberSet]);

  // Handle hint
  const handleHint = async () => {
    if (!puzzleId || hintsUsed >= MAX_HINTS) return;

    try {
      const response = await puzzlesApi.getHint(parseInt(puzzleId, 10), currentState);
      if ('position' in response && 'value' in response) {
        useHint(response.position, response.value);

        // Check if all cells are filled after hint (hint values are always correct)
        const updatedState = useGameStore.getState().currentState;
        if (!updatedState.includes('0')) {
          await checkGameCompletion();
        }
      }
    } catch (err: unknown) {
      console.error('Failed to get hint:', err);
    }
  };

  // Check for completion
  useEffect(() => {
    if (!isComplete || showCompletion || !puzzle) return;

    const handleCompletion = async () => {
      if (!puzzleId) {
        setShowCompletion(true);
        return;
      }

      // Always save to local storage (for both logged in and guest users)
      markPuzzleCompletedLocally(
        parseInt(puzzleId, 10),
        puzzle.difficulty as Difficulty,
        getLevelNumber(puzzle)
      );

      // If authenticated, also save to server
      if (isAuthenticated) {
        try {
          const response = await progressApi.complete(parseInt(puzzleId, 10), {
            currentState,
            timeSpent,
            hintsUsed,
            mistakes,
          });
          setCompletionScore(response.score);
        } catch (err) {
          console.error('Failed to save completion to server:', err);
        }
      }

      setShowCompletion(true);
    };

    handleCompletion();
  }, [isComplete, showCompletion, puzzleId, puzzle, isAuthenticated, currentState, timeSpent, hintsUsed, mistakes]);

  // Handle play again
  const handlePlayAgain = () => {
    setShowCompletion(false);
    reset();
  };

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !puzzle) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="glass-card p-8 text-center">
          <p className="text-red-400 mb-4">{error || '关卡不存在'}</p>
          <button onClick={() => navigate('/levels')} className="btn-primary">
            返回关卡选择
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-green-50 to-white flex flex-col">
      {/* Header */}
      <div className="px-3 sm:px-4 pt-2 sm:pt-4 pb-2 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          {/* Left: Back arrow */}
          <Link to="/levels" className="text-green-600 hover:text-green-700 p-1 -ml-1 touch-manipulation">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>

          {/* Center: Level number */}
          <div className="text-base sm:text-lg font-semibold text-gray-800">
            关卡{getLevelNumber(puzzle)}
          </div>

          {/* Right: Star and Settings icons */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button className="text-green-600 hover:text-green-700 p-1 touch-manipulation">
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </button>
            <button className="text-green-600 hover:text-green-700 p-1 touch-manipulation">
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Game Status Row */}
        <GameStatus
          difficulty={puzzle.difficulty as Difficulty}
          levelNumber={getLevelNumber(puzzle)}
          mistakes={mistakes}
        />
      </div>

      {/* Game Board - flex-grow to take available space */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative px-3 sm:px-4 mb-3 sm:mb-4 flex-shrink-0"
      >
        <Board errorCells={errorCells} />
        <PauseOverlay />
      </motion.div>

      {/* Number Pad - Higher frequency, placed above */}
      <div className="px-3 sm:px-4 mb-4 sm:mb-6 flex-shrink-0">
        <NumberPad />
      </div>

      {/* Controls - Lower frequency, placed below with spacing */}
      <div className="px-3 sm:px-4 mb-3 sm:mb-4 pb-safe flex-shrink-0">
        <GameControls
          onHint={handleHint}
          hintsRemaining={MAX_HINTS - hintsUsed}
        />
      </div>

      {/* Completion Modal */}
      <CompletionModal
        isOpen={showCompletion}
        difficulty={puzzle.difficulty as Difficulty}
        levelNumber={getLevelNumber(puzzle)}
        timeSpent={timeSpent}
        hintsUsed={hintsUsed}
        mistakes={mistakes}
        score={completionScore}
        nextPuzzleId={nextPuzzleId}
        onPlayAgain={handlePlayAgain}
      />
    </div>
  );
}

